import { MODE_LABELS, type GameMode } from "../core/mode";
import { hasStoredSave, readSave, type SaveData } from "../core/save";
import { sound } from "../audio/sound-engine";
import {
  settingsManager,
  FPS_LIMITS,
  getPresetBatterySettings,
  getPresetBalancedSettings,
  getPresetMaxSettings,
  getDefaultSettings,
  type GameSettings,
} from "./settings";
export { MIN_RENDER_DISTANCE, MAX_RENDER_DISTANCE } from "./settings";
import { renderTitleLogo } from "./title-logo";

/** Everything the title screen chose for the next world. */
export interface WorldSetup {
  seed: number;
  mode: GameMode;
  renderDistance: number;
}

/** A fresh 31-bit seed, same range the game has always used for worlds. */
export function randomSeed(): number {
  return Math.floor(Math.random() * 2 ** 31);
}

/**
 * The title screen: start a world, read the controls, tweak laptop graphics or read
 * the blurb. Owns its sub-screens and the loading screen shown while the
 * starting area generates, so the game itself never has to know about them.
 */
export class MainMenu {
  /** Fired when the player commits to a new world ("Play"). */
  onPlay: ((setup: WorldSetup) => void) | null = null;
  /** Fired when the player resumes the saved world ("Continue"). */
  onContinue: ((save: SaveData) => void) | null = null;

  private readonly root: HTMLElement;
  private readonly screens = new Map<string, HTMLElement>();
  private readonly seedInput: HTMLInputElement;

  // Settings elements
  private readonly renderInput: HTMLInputElement;
  private readonly renderValue: HTMLElement;
  private readonly btnFpsLimit: HTMLButtonElement;
  private readonly fpsLimitValue: HTMLElement;
  private readonly btnGraphicsQuality: HTMLButtonElement;
  private readonly graphicsQualityValue: HTMLElement;
  private readonly btnSmoothLighting: HTMLButtonElement;
  private readonly smoothLightingValue: HTMLElement;
  private readonly fovSlider: HTMLInputElement;
  private readonly fovValue: HTMLElement;
  private readonly sensitivitySlider: HTMLInputElement;
  private readonly sensitivityValue: HTMLElement;
  private readonly btnViewBobbing: HTMLButtonElement;
  private readonly viewBobbingValue: HTMLElement;
  private readonly btnFovEffects: HTMLButtonElement;
  private readonly fovEffectsValue: HTMLElement;
  private readonly volumeSlider: HTMLInputElement;
  private readonly volumeValue: HTMLElement;
  private readonly btnMuteToggle: HTMLButtonElement;
  private readonly muteToggleValue: HTMLElement;
  private readonly btnFullscreenToggle: HTMLButtonElement;
  private readonly fullscreenToggleValue: HTMLElement;

  private readonly titleLogoElement: HTMLElement | null;
  private readonly loadingSub: HTMLElement;
  private readonly loadingNote: HTMLElement;
  private readonly warning: HTMLElement;
  private readonly continueButton: HTMLButtonElement;
  private readonly continueSub: HTMLElement;
  private readonly newWorldNote: HTMLElement;
  private readonly saveNote: HTMLElement;
  private readonly modeButtons: HTMLButtonElement[];

  private mode: GameMode = "creative";
  private current = "main";
  private visible = true;
  private loading = false;
  private inGameSettingsCallback: (() => void) | null = null;

  constructor(root: HTMLElement) {
    this.root = root;
    for (const name of ["main", "world", "howto", "settings", "about", "loading"]) {
      this.screens.set(name, must(root.querySelector<HTMLElement>(`#screen-${name}`)));
    }
    this.seedInput = must(root.querySelector<HTMLInputElement>("#seed-input"));

    // Settings elements
    this.renderInput = must(root.querySelector<HTMLInputElement>("#render-distance"));
    this.renderValue = must(root.querySelector<HTMLElement>("#render-distance-value"));
    this.btnFpsLimit = must(root.querySelector<HTMLButtonElement>("#btn-fps-limit"));
    this.fpsLimitValue = must(root.querySelector<HTMLElement>("#fps-limit-value"));
    this.btnGraphicsQuality = must(root.querySelector<HTMLButtonElement>("#btn-graphics-quality"));
    this.graphicsQualityValue = must(root.querySelector<HTMLElement>("#graphics-quality-value"));
    this.btnSmoothLighting = must(root.querySelector<HTMLButtonElement>("#btn-smooth-lighting"));
    this.smoothLightingValue = must(root.querySelector<HTMLElement>("#smooth-lighting-value"));
    this.fovSlider = must(root.querySelector<HTMLInputElement>("#fov-slider"));
    this.fovValue = must(root.querySelector<HTMLElement>("#fov-value"));
    this.sensitivitySlider = must(root.querySelector<HTMLInputElement>("#sensitivity-slider"));
    this.sensitivityValue = must(root.querySelector<HTMLElement>("#sensitivity-value"));
    this.btnViewBobbing = must(root.querySelector<HTMLButtonElement>("#btn-view-bobbing"));
    this.viewBobbingValue = must(root.querySelector<HTMLElement>("#view-bobbing-value"));
    this.btnFovEffects = must(root.querySelector<HTMLButtonElement>("#btn-fov-effects"));
    this.fovEffectsValue = must(root.querySelector<HTMLElement>("#fov-effects-value"));
    this.volumeSlider = must(root.querySelector<HTMLInputElement>("#volume-slider"));
    this.volumeValue = must(root.querySelector<HTMLElement>("#volume-value"));
    this.btnMuteToggle = must(root.querySelector<HTMLButtonElement>("#btn-mute-toggle"));
    this.muteToggleValue = must(root.querySelector<HTMLElement>("#mute-toggle-value"));
    this.btnFullscreenToggle = must(root.querySelector<HTMLButtonElement>("#btn-fullscreen-toggle"));
    this.fullscreenToggleValue = must(root.querySelector<HTMLElement>("#fullscreen-toggle-value"));

    this.titleLogoElement = root.querySelector<HTMLElement>("#game-title-logo");
    this.loadingSub = must(root.querySelector<HTMLElement>("#loading-sub"));
    this.loadingNote = must(root.querySelector<HTMLElement>("#loading-note"));
    this.warning = must(root.querySelector<HTMLElement>("#webgl-warning"));
    this.continueButton = must(root.querySelector<HTMLButtonElement>("#btn-continue"));
    this.continueSub = must(root.querySelector<HTMLElement>("#continue-sub"));
    this.newWorldNote = must(root.querySelector<HTMLElement>("#new-world-note"));
    this.saveNote = must(root.querySelector<HTMLElement>("#save-note"));
    this.modeButtons = [...root.querySelectorAll<HTMLButtonElement>(".mode-btn")];

    this.seedInput.value = String(randomSeed());
    this.initTitleLogo();
    this.syncSettingsUI(settingsManager.settings);

    this.bind("#btn-singleplayer", () => this.showScreen("world"));
    this.bind("#btn-continue", () => {
      if (this.loading) return;
      const save = readSave();
      if (save) this.onContinue?.(save);
      else this.refreshSaveState();
    });
    this.bind("#btn-howto", () => this.showScreen("howto"));
    this.bind("#btn-settings", () => this.showScreen("settings"));
    this.bind("#btn-about", () => this.showScreen("about"));
    this.bind("#btn-world-back", () => this.showScreen("main"));
    this.bind("#btn-howto-back", () => this.showScreen("main"));
    this.bind("#btn-settings-back", () => {
      if (this.inGameSettingsCallback) {
        const cb = this.inGameSettingsCallback;
        this.inGameSettingsCallback = null;
        this.hide();
        cb();
      } else {
        this.showScreen("main");
      }
    });
    this.bind("#btn-about-back", () => this.showScreen("main"));
    this.bind("#btn-random-seed", () => {
      this.seedInput.value = String(randomSeed());
    });
    this.bind("#btn-create", () => this.start());

    // Settings Quick Presets
    this.bind("#preset-battery", () => {
      settingsManager.setAll(getPresetBatterySettings());
      this.syncSettingsUI(settingsManager.settings);
    });
    this.bind("#preset-balanced", () => {
      settingsManager.setAll(getPresetBalancedSettings());
      this.syncSettingsUI(settingsManager.settings);
    });
    this.bind("#preset-max", () => {
      settingsManager.setAll(getPresetMaxSettings());
      this.syncSettingsUI(settingsManager.settings);
    });
    this.bind("#preset-default", () => {
      settingsManager.setAll(getDefaultSettings());
      this.syncSettingsUI(settingsManager.settings);
    });

    for (const btn of this.modeButtons) {
      const mode: GameMode = btn.dataset.mode === "survival" ? "survival" : "creative";
      btn.addEventListener("click", () => {
        sound.init();
        sound.playUiClick();
        this.setMode(mode);
      });
    }

    // Settings inputs & toggles
    this.renderInput.addEventListener("input", () => {
      settingsManager.update({ renderDistance: Number(this.renderInput.value) });
      this.syncSettingsUI(settingsManager.settings);
    });

    this.btnFpsLimit.addEventListener("click", () => {
      sound.init();
      sound.playUiClick();
      const current = settingsManager.settings.maxFps;
      const idx = FPS_LIMITS.indexOf(current as 30 | 60 | 120 | 0);
      const next = FPS_LIMITS[(idx + 1) % FPS_LIMITS.length];
      settingsManager.update({ maxFps: next });
      this.syncSettingsUI(settingsManager.settings);
    });

    this.btnGraphicsQuality.addEventListener("click", () => {
      sound.init();
      sound.playUiClick();
      const next = settingsManager.settings.graphicsQuality === "fast" ? "fancy" : "fast";
      settingsManager.update({ graphicsQuality: next });
      this.syncSettingsUI(settingsManager.settings);
    });

    this.btnSmoothLighting.addEventListener("click", () => {
      sound.init();
      sound.playUiClick();
      settingsManager.update({ smoothLighting: !settingsManager.settings.smoothLighting });
      this.syncSettingsUI(settingsManager.settings);
    });

    this.fovSlider.addEventListener("input", () => {
      settingsManager.update({ fov: Number(this.fovSlider.value) });
      this.syncSettingsUI(settingsManager.settings);
    });

    this.sensitivitySlider.addEventListener("input", () => {
      const factor = Number(this.sensitivitySlider.value) / 100;
      settingsManager.update({ mouseSensitivity: factor });
      this.syncSettingsUI(settingsManager.settings);
    });

    this.btnViewBobbing.addEventListener("click", () => {
      sound.init();
      sound.playUiClick();
      settingsManager.update({ viewBobbing: !settingsManager.settings.viewBobbing });
      this.syncSettingsUI(settingsManager.settings);
    });

    this.btnFovEffects.addEventListener("click", () => {
      sound.init();
      sound.playUiClick();
      settingsManager.update({ fovEffects: !settingsManager.settings.fovEffects });
      this.syncSettingsUI(settingsManager.settings);
    });

    this.volumeSlider.addEventListener("input", () => {
      const vol = Number(this.volumeSlider.value) / 100;
      settingsManager.update({ masterVolume: vol });
      this.syncSettingsUI(settingsManager.settings);
    });

    this.btnMuteToggle.addEventListener("click", () => {
      sound.init();
      sound.playUiClick();
      settingsManager.update({ soundMuted: !settingsManager.settings.soundMuted });
      this.syncSettingsUI(settingsManager.settings);
    });

    this.btnFullscreenToggle.addEventListener("click", () => {
      sound.init();
      sound.playUiClick();
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => {});
      } else {
        document.exitFullscreen().catch(() => {});
      }
      setTimeout(() => this.updateFullscreenLabel(), 100);
    });

    document.addEventListener("fullscreenchange", () => this.updateFullscreenLabel());

    this.refreshSaveState();
    window.addEventListener("keydown", this.onKeyDown);
  }

  /** Render distance currently chosen on the settings screen. */
  get renderDistance(): number {
    return settingsManager.settings.renderDistance;
  }

  /** Seed a world from the URL (or any other caller) into the menu fields. */
  preset(options: { mode?: GameMode; seed?: number | null; renderDistance?: number | null }): void {
    if (options.mode) this.setMode(options.mode);
    if (options.seed !== undefined && options.seed !== null && Number.isFinite(options.seed)) {
      this.seedInput.value = String(options.seed);
    }
    if (
      options.renderDistance !== undefined &&
      options.renderDistance !== null &&
      Number.isFinite(options.renderDistance)
    ) {
      settingsManager.update({ renderDistance: options.renderDistance });
      this.syncSettingsUI(settingsManager.settings);
    }
  }

  /** Show the title screen, back on the main page. */
  show(): void {
    this.inGameSettingsCallback = null;
    this.loading = false;
    this.visible = true;
    this.root.hidden = false;
    this.initTitleLogo();
    this.refreshSaveState();
    this.syncSettingsUI(settingsManager.settings);
    this.showScreen("main");
  }

  /** Open the Settings screen directly from an in-game pause menu. */
  openSettingsFromGame(onDone: () => void): void {
    this.inGameSettingsCallback = onDone;
    this.loading = false;
    this.visible = true;
    this.root.hidden = false;
    this.syncSettingsUI(settingsManager.settings);
    this.showScreen("settings");
  }

  hide(): void {
    this.loading = false;
    this.visible = false;
    this.root.hidden = true;
  }

  /** Swap the title screen for the loading screen while a world generates. */
  showLoading(setup: WorldSetup, continued = false): void {
    this.loading = true;
    this.visible = true;
    this.root.hidden = false;
    this.loadingSub.textContent =
      `${continued ? "Continue · " : ""}seed ${setup.seed} · ${MODE_LABELS[setup.mode]} · render distance ${setup.renderDistance}`;
    this.setLoadingNote(null);
    this.showScreen("loading");
  }

  setLoadingNote(note: string | null): void {
    this.loadingNote.hidden = note === null;
    this.loadingNote.textContent = note ?? "";
  }

  setWarning(text: string | null): void {
    this.warning.hidden = text === null;
    this.warning.textContent = text ?? "";
  }

  // --- internals -----------------------------------------------------------

  private initTitleLogo(): void {
    if (this.titleLogoElement instanceof HTMLCanvasElement) {
      renderTitleLogo(this.titleLogoElement);
    }
  }

  private syncSettingsUI(s: GameSettings): void {
    // Render Distance
    this.renderInput.value = String(s.renderDistance);
    let distLabel = `${s.renderDistance} chunks`;
    if (s.renderDistance <= 2) distLabel += " (Tiny · Battery)";
    else if (s.renderDistance <= 4) distLabel += " (Short)";
    else if (s.renderDistance <= 6) distLabel += " (Normal · Laptop Default)";
    else if (s.renderDistance <= 8) distLabel += " (Standard)";
    else if (s.renderDistance <= 12) distLabel += " (Far)";
    else distLabel += " (Extreme)";
    this.renderValue.textContent = distLabel;

    // FPS Limit
    let fpsText = `${s.maxFps} FPS`;
    if (s.maxFps === 30) fpsText = "30 FPS (Battery Saver)";
    else if (s.maxFps === 60) fpsText = "60 FPS (Balanced · Laptop Default)";
    else if (s.maxFps === 120) fpsText = "120 FPS (High Refresh)";
    else if (s.maxFps === 0) fpsText = "Unlimited (Max Performance)";
    this.fpsLimitValue.textContent = fpsText;

    // Graphics Quality
    this.graphicsQualityValue.textContent =
      s.graphicsQuality === "fast" ? "Fast (1.0x DPI · High FPS)" : "Fancy (Native Retina)";

    // Smooth Lighting
    this.smoothLightingValue.textContent = s.smoothLighting ? "ON (Ambient Occlusion)" : "OFF (Flat Shading)";

    // FOV
    this.fovSlider.value = String(s.fov);
    let fovText = `${s.fov}°`;
    if (s.fov === 75) fovText += " (Normal)";
    else if (s.fov >= 100) fovText += " (Quake Pro)";
    this.fovValue.textContent = fovText;

    // Sensitivity
    const sensPercent = Math.round(s.mouseSensitivity * 100);
    this.sensitivitySlider.value = String(sensPercent);
    let sensText = `${sensPercent}%`;
    if (sensPercent === 100) sensText += " (Default)";
    else if (sensPercent < 60) sensText += " (Slow / Touchpad)";
    else if (sensPercent > 140) sensText += " (Fast / Gaming)";
    this.sensitivityValue.textContent = sensText;

    // View Bobbing
    this.viewBobbingValue.textContent = s.viewBobbing ? "ON" : "OFF";

    // FOV Effects
    this.fovEffectsValue.textContent = s.fovEffects ? "ON" : "OFF";

    // Volume
    const volPercent = Math.round(s.masterVolume * 100);
    this.volumeSlider.value = String(volPercent);
    this.volumeValue.textContent = `${volPercent}%`;

    // Mute
    this.muteToggleValue.textContent = s.soundMuted ? "Muted" : "Enabled";

    // Fullscreen
    this.updateFullscreenLabel();
  }

  private updateFullscreenLabel(): void {
    const isFull = !!document.fullscreenElement;
    this.fullscreenToggleValue.textContent = isFull ? "ON" : "OFF";
  }

  private start(): void {
    if (this.loading) return;
    const raw = this.seedInput.value.trim();
    const parsed = Number(raw);
    const seed = raw !== "" && Number.isFinite(parsed) ? Math.trunc(parsed) : randomSeed();
    this.seedInput.value = String(seed);
    this.onPlay?.({ seed, mode: this.mode, renderDistance: this.renderDistance });
  }

  private setMode(mode: GameMode): void {
    this.mode = mode;
    for (const btn of this.modeButtons) {
      btn.classList.toggle("active", btn.dataset.mode === mode);
    }
  }

  private refreshSaveState(): void {
    const stored = hasStoredSave();
    const save = stored ? readSave() : null;
    this.continueButton.hidden = save === null;
    this.newWorldNote.hidden = !stored;
    if (save) {
      this.continueSub.textContent = `${MODE_LABELS[save.world.mode]} · seed ${save.world.seed}`;
      this.saveNote.hidden = true;
      return;
    }
    this.continueSub.textContent = "resume your saved world";
    this.saveNote.hidden = !stored;
    this.saveNote.textContent = stored
      ? "An existing save is from an incompatible version or is damaged. Starting a new world will replace it."
      : "";
  }

  private showScreen(name: string): void {
    if (!this.screens.has(name)) return;
    this.current = name;
    for (const [key, el] of this.screens) el.hidden = key !== name;
    if (name === "loading") return;
    this.controls()[0]?.focus({ preventScroll: true });
  }

  private controls(): HTMLElement[] {
    const screen = this.screens.get(this.current);
    if (!screen) return [];
    return [...screen.querySelectorAll<HTMLElement>("button, input")].filter(
      (el) => !el.hidden && el.closest("[hidden]") === null && getComputedStyle(el).display !== "none",
    );
  }

  private bind(selector: string, handler: () => void): void {
    const el = this.root.querySelector<HTMLButtonElement>(selector);
    if (!el) return;
    el.onclick = () => {
      sound.init();
      sound.playUiClick();
      handler();
    };
  }

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (!this.visible || this.loading) return;

    if (e.key === "Escape") {
      if (this.current !== "main") {
        e.preventDefault();
        if (this.inGameSettingsCallback) {
          const cb = this.inGameSettingsCallback;
          this.inGameSettingsCallback = null;
          this.hide();
          cb();
        } else {
          this.showScreen("main");
        }
      }
      return;
    }

    if (e.key === "Enter" && this.current === "world" && document.activeElement === this.seedInput) {
      e.preventDefault();
      this.start();
      return;
    }

    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    if (document.activeElement instanceof HTMLInputElement && document.activeElement.type !== "button") return;

    const focusable = this.controls();
    if (focusable.length === 0) return;
    e.preventDefault();
    const step = e.key === "ArrowDown" ? 1 : -1;
    const index = document.activeElement instanceof HTMLElement ? focusable.indexOf(document.activeElement) : -1;
    const base = index < 0 ? (step > 0 ? -1 : 0) : index;
    focusable[(base + step + focusable.length) % focusable.length].focus({ preventScroll: true });
  };
}

function must<T extends Element>(el: T | null): T {
  if (!el) throw new Error("menu element missing from index.html");
  return el;
}
